import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTenant } from '../context/TenantContext';
import WorkflowEditor from '../components/WorkflowEditor';
import WorkflowRunner from '../components/WorkflowRunner';

export default function Workflows({ onBack, defaultModel }) {
  const { user } = useAuth();
  const { activeTenant, tenantFetch } = useTenant();
  const [workflows, setWorkflows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // workflow object or { __new: true }
  const [running, setRunning] = useState(null);
  const [runnerInitialTab, setRunnerInitialTab] = useState('run');

  const isTenantAdmin = activeTenant?.role === 'admin' || activeTenant?.role === 'owner';

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await tenantFetch('/api/workflows');
      const data = await res.json();
      if (res.ok) setWorkflows(data.workflows || []);
    } finally {
      setLoading(false);
    }
  }, [tenantFetch]);

  useEffect(() => { load(); }, [load]);

  const canEdit = (w) => w.ownerId === user._id || isTenantAdmin;

  const handleDelete = async (w) => {
    if (!window.confirm(`Workflow "${w.name}" wirklich löschen?`)) return;
    const res = await tenantFetch(`/api/workflows/${w._id}`, { method: 'DELETE' });
    if (res.ok) await load();
  };

  const handleClone = async (w) => {
    const res = await tenantFetch(`/api/workflows/${w._id}/clone`, { method: 'POST' });
    if (res.ok) await load();
  };

  const handleToggleLock = async (w) => {
    const path = w.locked ? 'unlock' : 'lock';
    const res = await tenantFetch(`/api/workflows/${w._id}/${path}`, { method: 'POST' });
    if (res.ok) await load();
    else {
      const data = await res.json().catch(() => ({}));
      alert(data.error || 'Sperren/Entsperren fehlgeschlagen.');
    }
  };

  const formatSchedule = (s) => {
    if (!s || !s.enabled) return null;
    const days = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];
    const start = s.startTime || s.timeOfDay || '00:00';
    const window = s.endTime ? `${start}–${s.endTime}` : start;
    if (s.frequency === 'hourly') return `stündlich :${start.split(':')[1]}${s.endTime ? ` (${start}–${s.endTime})` : ''}`;
    if (s.frequency === 'daily') return `täglich ${window}`;
    if (s.frequency === 'weekly') return `${days[s.dayOfWeek]} ${window}`;
    return 'aktiv';
  };

  const handleSaved = async (saved) => {
    setEditing(null);
    await load();
    if (saved && !running) setRunning(null);
  };

  return (
    <div className="workflows-page">
      <div className="workflows-header">
        <button className="icon-btn" onClick={onBack} title="Zurück">←</button>
        <h2>Workflows</h2>
        <div style={{ flex: 1 }} />
        <button
          className="primary-btn"
          onClick={() => setEditing({ __new: true })}
        >
          + Neuer Workflow
        </button>
      </div>

      <div className="workflows-body">
        {loading ? (
          <p>Lädt…</p>
        ) : workflows.length === 0 ? (
          <div className="workflows-empty">
            <p>Noch keine Workflows.</p>
            <p>Beschreibe einen Geschäftsprozess in natürlicher Sprache, und die KI baut den Workflow für dich.</p>
            <button className="primary-btn" onClick={() => setEditing({ __new: true })}>
              Ersten Workflow erstellen
            </button>
          </div>
        ) : (
          <ul className="workflows-list">
            {workflows.map(w => (
              <li key={w._id} className={`workflow-card ${w.locked ? 'locked' : ''}`}>
                <div className="workflow-card-main">
                  <div className="workflow-card-title">
                    {w.name}
                    {w.visibility === 'tenant' && <span className="workflow-shared-tag">Geteilt</span>}
                    {w.ownerId !== user._id && <span className="workflow-foreign-tag">von Mitglied</span>}
                    {w.locked && <span className="workflow-locked-tag">🔒 gesperrt</span>}
                    {w.schedule?.enabled && (
                      <span className="workflow-schedule-tag">⏱ {formatSchedule(w.schedule)}</span>
                    )}
                  </div>
                  {w.description && <div className="workflow-card-desc">{w.description}</div>}
                  <div className="workflow-card-meta">
                    {w.steps?.length || 0} Schritt(e)
                    {' · '}
                    aktualisiert {new Date(w.updatedAt).toLocaleDateString()}
                  </div>
                </div>
                <div className="workflow-card-actions">
                  <button className="link-btn" onClick={() => { setRunnerInitialTab('run'); setRunning(w); }} disabled={w.locked} title={w.locked ? 'Workflow ist gesperrt' : ''}>Ausführen</button>
                  <button className="link-btn" onClick={() => { setRunnerInitialTab('history'); setRunning(w); }} title="Verlauf anzeigen">📜 Verlauf</button>
                  <button className="link-btn" onClick={() => setEditing(w)}>
                    {canEdit(w) && !w.locked ? 'Bearbeiten' : 'Anzeigen'}
                  </button>
                  <button className="link-btn" onClick={() => handleClone(w)}>Kopieren</button>
                  {isTenantAdmin && (
                    <button className="link-btn" onClick={() => handleToggleLock(w)}>
                      {w.locked ? '🔓 Entsperren' : '🔒 Sperren'}
                    </button>
                  )}
                  {canEdit(w) && !w.locked && (
                    <button className="link-btn danger" onClick={() => handleDelete(w)}>Löschen</button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {editing && (
        <WorkflowEditor
          initial={editing.__new ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={handleSaved}
          defaultModel={defaultModel}
          readOnly={!editing.__new && (!canEdit(editing) || editing.locked)}
        />
      )}

      {running && (
        <WorkflowRunner
          workflow={running}
          onClose={() => setRunning(null)}
          defaultModel={defaultModel}
          initialTab={runnerInitialTab}
        />
      )}
    </div>
  );
}
